import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';

interface CopyButtonProps {
    /** Text copied to the clipboard on click */
    value: string;
    className?: string;
}

const CopyButton = ({ value, className }: CopyButtonProps) => {
    const { t } = useTranslation();
    const [isCopied, setIsCopied] = useState(false);

    useEffect(() => {
        if (!isCopied) return;
        const timeout = setTimeout(() => setIsCopied(false), 2000);
        return () => clearTimeout(timeout);
    }, [isCopied]);

    const handleCopy = async (e: React.MouseEvent) => {
        e.stopPropagation();
        await navigator.clipboard.writeText(value);
        setIsCopied(true);
    };

    return (
        <TooltipProvider delayDuration={300}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                            'h-6 w-6 shrink-0 text-muted-foreground/50 hover:text-muted-foreground',
                            className
                        )}
                        onClick={handleCopy}
                        aria-label={t('copyButton.copy')}
                    >
                        {isCopied ? (
                            <Check className="h-3.5 w-3.5 text-green-600" />
                        ) : (
                            <Copy className="h-3.5 w-3.5" />
                        )}
                    </Button>
                </TooltipTrigger>
                <TooltipContent>
                    {isCopied ? t('copyButton.copied') : t('copyButton.copy')}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export default CopyButton;
